import { useState, useEffect, useCallback, useContext } from "react";
import { Table, Button, Modal, Form, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import requestAxios from "../../api/requestAxios";
import { PermissionContext } from "../../hooks/PermissionContext";

const ReschedulingConfig = () => {
  const { hasPermission } = useContext(PermissionContext);

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newIdCompany, setNewIdCompany] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const data = await requestAxios("/api/v1/company-management", { method: "GET" });
      setCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      Swal.fire("Error", "No se pudieron cargar las empresas configuradas", "error");
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleAdd = async () => {
    const idCompany = parseInt(newIdCompany, 10);
    if (!idCompany || idCompany < 1) {
      Swal.fire("Campo inválido", "Ingrese un ID de empresa válido (número positivo)", "warning");
      return;
    }

    try {
      setSaving(true);
      const response = await requestAxios("/api/v1/company-management", {
        method: "POST",
        body: { idCompany },
        headers: { "Content-Type": "application/json" },
      });
      const companyName = response?.data?.companyName;
      Swal.fire("Empresa agregada", `La empresa ${idCompany}${companyName ? ` (${companyName})` : ''} se registró correctamente`, "success");
      setShowModal(false);
      setNewIdCompany("");
      fetchCompanies();
    } catch (error) {
      const status = error?.response?.status;
      if (status === 409) {
        Swal.fire("Empresa duplicada", `La empresa ${idCompany} ya se encuentra registrada`, "warning");
      } else if (status === 400) {
        const backendMessage = error?.response?.data?.message || "";
        const nameMatch = backendMessage.match(/\(([^)]+)\)/);
        const companyName = nameMatch ? nameMatch[1] : error?.response?.data?.companyName;
        Swal.fire(
          "Empresa inactiva",
          `La empresa ${idCompany}${companyName ? ` (${companyName})` : ''} está inactiva en el sistema`,
          "error"
        );
      } else if (status === 404) {
        Swal.fire("Empresa no encontrada", `La empresa ${idCompany} no existe en el sistema`, "error");
      } else {
        Swal.fire("Error", "No se pudo agregar la empresa", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (company) => {
    const { idCompany, companyName } = company;
    Swal.fire({
      title: "¿Eliminar empresa?",
      text: `La empresa ${idCompany}${companyName ? ` (${companyName})` : ''} será desactivada del módulo de reprogramación`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e74a3b",
      cancelButtonColor: "#858796",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await requestAxios(`/api/v1/company-management/${idCompany}`, { method: "DELETE" });
          Swal.fire("Empresa eliminada", `La empresa ${idCompany}${companyName ? ` (${companyName})` : ''} se desactivó correctamente`, "success");
          fetchCompanies();
        } catch (error) {
          if (error?.response?.status === 404) {
            Swal.fire("No encontrada", `La empresa ${idCompany} no existe`, "error");
          } else {
            Swal.fire("Error", "No se pudo eliminar la empresa", "error");
          }
        }
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("es-CO", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <h6 className="m-0 font-weight-bold text-primary">
          Empresas gestionadas dinámicamente
        </h6>
        <Button variant="primary" size="sm" onClick={() => setShowModal(true)}>
          <i className="fas fa-plus fa-sm mr-1"></i> Agregar empresa
        </Button>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : companies.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <p className="mb-0">No hay empresas configuradas</p>
            <small>Use el botón "Agregar empresa" para registrar una nueva</small>
          </div>
        ) : (
          <Table striped bordered hover responsive size="sm">
            <thead className="thead-light">
              <tr>
                <th>ID</th>
                <th>ID Empresa</th>
                <th>Cliente</th>
                <th>Tipo de gestión</th>
                <th>Activo</th>
                <th>Fecha de creación</th>
                <th style={{ width: "100px" }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((company) => (
                <tr key={company.id}>
                  <td>{company.id}</td>
                  <td>{company.idCompany}</td>
                  <td>{company.companyName}</td>
                  <td>{company.managementType}</td>
                  <td>
                    {company.active ? (
                      <span className="badge badge-success">Sí</span>
                    ) : (
                      <span className="badge badge-secondary">No</span>
                    )}
                  </td>
                  <td>{formatDate(company.createdAt)}</td>
                  <td className="text-center">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(company)}
                      disabled={!company.active}
                      title={!company.active ? "Ya está desactivada" : "Desactivar empresa"}
                    >
                      <i className="fas fa-trash-alt"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      <Modal show={showModal} onHide={() => { if (!saving) { setShowModal(false); setNewIdCompany(""); } }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Agregar empresa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleAdd();
            }}
          >
            <Form.Group>
              <Form.Label>ID de empresa</Form.Label>
              <Form.Control
                type="number"
                min="1"
                step="1"
                placeholder="Ej: 63"
                value={newIdCompany}
                onChange={(e) => setNewIdCompany(e.target.value)}
                disabled={saving}
                autoFocus
              />
              <Form.Text className="text-muted">
                ID numérico de la empresa en el sistema (debe ser único)
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowModal(false); setNewIdCompany(""); }} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleAdd} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="mr-1" /> Guardando...
              </>
            ) : (
              "Guardar"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ReschedulingConfig;
